/* ============================================================================
   M5.2 — Courier Payment Tracking  (route /supply/payments)
   Payments to courier partners against reconciled invoices. KPI strip + table.
   "Record Payment" is a ProShip-Admin capability (this replica runs as admin):
   a Modal that picks a reconciled invoice and records a net transfer.
   ========================================================================== */
import React, {useState} from 'react'
import PropTypes from 'prop-types'
import {useNavigate} from 'react-router-dom'
import AddIcon from '@mui/icons-material/Add'
import AccountBalanceOutlinedIcon from '@mui/icons-material/AccountBalanceOutlined'
import CheckCircleOutlinedIcon from '@mui/icons-material/CheckCircleOutlined'
import AccessTimeOutlinedIcon from '@mui/icons-material/AccessTimeOutlined'
import PaymentsOutlinedIcon from '@mui/icons-material/PaymentsOutlined'
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined'
import ReportProblemOutlinedIcon from '@mui/icons-material/ReportProblemOutlined'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import PageTitle from '../../../components/PageTitle'
import {
  PageHeader,
  KpiStrip,
  SupplyTable,
  Badge,
  Modal,
  useToast,
  SearchSelect,
  inr,
  money,
  fmtDate,
  TODAY,
} from '../components'
import {DUMMY_PAYMENTS, DUMMY_INVOICES} from '../dummyData'
import {FINAL, BANKS, VertChip, ME} from './reconShared'

const IS_ADMIN = true

export default function Payments() {
  const toast = useToast()
  const navigate = useNavigate()
  const [tick, setTick] = useState(0)
  const refresh = () => setTick((t) => t + 1)
  const [record, setRecord] = useState(false)

  void tick // re-derive trigger after recording a payment / status change

  // page is scoped to the D2C vertical
  const rows = DUMMY_PAYMENTS.filter((x) => x.vertical === 'D2C')
  const confirmed = rows.filter((x) => x.status === 'Confirmed')
  const inProg = rows.filter((x) => x.status === 'Initiated').length
  const totalPaid = confirmed.reduce((s, x) => s + (x.amount || 0), 0)

  const kpis = [
    {
      label: 'Payments',
      value: rows.length,
      icon: <AccountBalanceOutlinedIcon />,
      accent: '#1976D2',
      sub: 'in D2C',
    },
    {
      label: 'Confirmed',
      value: confirmed.length,
      icon: <CheckCircleOutlinedIcon />,
      accent: '#2E7D32',
      sub: 'settled transfers',
    },
    {
      label: 'In Progress',
      value: inProg,
      icon: <AccessTimeOutlinedIcon />,
      accent: inProg ? '#B45309' : '#6B7280',
      sub: 'initiated',
    },
    {
      label: 'Total Paid Out',
      value: money(totalPaid),
      icon: <PaymentsOutlinedIcon />,
      accent: '#1565C0',
      sub: 'confirmed only',
    },
  ]

  const setStatus = (x, status, msg) => {
    x.status = status
    toast(msg, 'success')
    refresh()
  }

  const columns = [
    {key: 'partner', label: 'Partner', render: (x) => <b>{x.partner}</b>},
    {
      key: 'vertical',
      label: 'Vertical',
      sortable: false,
      render: (x) => <VertChip vertical={x.vertical} />,
    },
    {
      key: 'invoiceRef',
      label: 'Invoice Reference',
      render: (x) => (
        <span
          className='lnk'
          role='link'
          tabIndex={0}
          onClick={() => navigate('/supply/invoice-recon')}
          onKeyDown={(e) => (e.key === 'Enter' ? navigate('/supply/invoice-recon') : null)}
        >
          <span className='mono'>{x.invoiceRef}</span>
        </span>
      ),
    },
    {
      key: 'amount',
      label: 'Invoice Amount',
      align: 'right',
      value: (x) => x.amount,
      render: (x) => <b>{inr(x.amount)}</b>,
    },
    {key: 'date', label: 'Payment Date', value: (x) => x.date, render: (x) => fmtDate(x.date)},
    {
      key: 'txn',
      label: 'Transaction ID',
      sortable: false,
      render: (x) => <span className='mono'>{x.txn}</span>,
    },
    {key: 'status', label: 'Payment Status', render: (x) => <Badge status={x.status} />},
    {
      key: 'paidBy',
      label: 'Paid By',
      render: (x) => <span style={{fontSize: 12}}>{x.paidBy}</span>,
    },
  ]

  return (
    <>
      <PageTitle>{'Courier Payment Tracking'}</PageTitle>

      <PageHeader
        title='Courier Payment Tracking'
        sub='Payments made to courier partners against reconciled invoices. Showing D2C payments.'
        actions={
          <button className='btn btn-primary' onClick={() => setRecord(true)}>
            <AddIcon style={{width: 16, height: 16}} />
            Record Payment
          </button>
        }
      />

      <div className='warnbox info' style={{marginBottom: 16}}>
        <InfoOutlinedIcon />
        <div>
          Payment listing. Net payable reflects invoice value less any credit notes from
          reconciliation. Full payment↔invoice auto-match is coming soon.
        </div>
      </div>

      <KpiStrip cards={kpis} />

      <SupplyTable
        columns={columns}
        rows={rows}
        rowKey={(x) => x.id}
        search={(r, q) =>
          [r.partner, r.invoiceRef, r.txn, r.paidBy, String(r.amount)].some(
            (v) => String(v).toLowerCase().indexOf(q) >= 0
          )
        }
        searchPlaceholder='Search by partner, invoice ref or transaction ID...'
        defaultSort={{key: 'date', dir: 'desc'}}
        exportName='courier-payments'
        emptyText='No payments recorded yet.'
        filters={[
          {
            key: 'status',
            label: 'Payment Status',
            options: [
              {value: 'Initiated', label: 'Initiated'},
              {value: 'Confirmed', label: 'Confirmed'},
              {value: 'Failed', label: 'Failed'},
            ],
            predicate: (x, sel) => sel.has(x.status),
          },
        ]}
        rowActions={(x) => [
          {
            label: 'View invoice',
            icon: <ReceiptLongOutlinedIcon fontSize='small' />,
            onClick: () => navigate('/supply/invoice-recon'),
          },
          IS_ADMIN && x.status === 'Initiated' ? {sep: true} : null,
          IS_ADMIN && x.status === 'Initiated'
            ? {
                label: 'Mark confirmed',
                icon: <CheckCircleOutlinedIcon fontSize='small' />,
                onClick: () => setStatus(x, 'Confirmed', 'Payment ' + x.txn + ' confirmed.'),
              }
            : null,
          IS_ADMIN && x.status === 'Initiated'
            ? {
                label: 'Mark failed',
                icon: <ReportProblemOutlinedIcon fontSize='small' />,
                danger: true,
                onClick: () => setStatus(x, 'Failed', 'Payment ' + x.txn + ' failed.'),
              }
            : null,
        ]}
      />

      <RecordPaymentModal
        open={record}
        onClose={() => setRecord(false)}
        onRecorded={refresh}
        toast={toast}
      />
    </>
  )
}

/* ── Record Payment modal (admin) ── */
function RecordPaymentModal({open, onClose, onRecorded, toast}) {
  const [invId, setInvId] = useState('')
  const [txn, setTxn] = useState('')
  const [date, setDate] = useState(TODAY.toISOString().slice(0, 10))
  const [bank, setBank] = useState('')
  const [err, setErr] = useState({})

  React.useEffect(() => {
    if (open) {
      setInvId('')
      setTxn('')
      setDate(TODAY.toISOString().slice(0, 10))
      setBank('')
      setErr({})
    }
  }, [open])

  const eligible = DUMMY_INVOICES.filter(
    (i) => i.vertical === 'D2C' && (i.reconStatus === 'No Dispute' || i.reconStatus === FINAL)
  )

  const submit = () => {
    const e = {}
    if (!invId) e.inv = true
    if (!txn.trim()) e.txn = true
    if (!date) e.date = true
    if (!bank) e.bank = true
    if (Object.keys(e).length) {
      setErr(e)
      toast('Please complete all required fields.', 'error')
      return false
    }
    const inv = DUMMY_INVOICES.find((i) => i.id === invId) || {}
    const net = (inv.billedAmount || 0) - (inv.cnAmount || 0)
    DUMMY_PAYMENTS.unshift({
      id: 'PAY-' + String(DUMMY_PAYMENTS.length + 1).padStart(3, '0'),
      partner: inv.partner,
      partnerId: inv.partnerId,
      vertical: inv.vertical,
      invoiceRef: inv.id,
      amount: net,
      date,
      txn: txn.trim(),
      status: 'Initiated',
      paidBy: ME,
    })
    toast(
      'Payment recorded for ' +
        (inv.partner || 'partner') +
        ' (' +
        inr(net) +
        ' net) via ' +
        bank +
        '.',
      'success'
    )
    onRecorded()
    return undefined
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title='Record Payment'
      icon={<PaymentsOutlinedIcon fontSize='small' />}
      width={560}
      actions={[
        {label: 'Cancel', onClick: onClose},
        {
          label: 'Record Payment',
          primary: true,
          icon: <PaymentsOutlinedIcon fontSize='small' />,
          onClick: submit,
        },
      ]}
    >
      <div className='warnbox info' style={{marginBottom: 14}}>
        <InfoOutlinedIcon />
        <div>
          Record a transfer to a courier. Reconciled invoices (No Dispute or Closed &amp; Shared
          with Finance) are payable; net of any credit note.
        </div>
      </div>

      <div className={'field' + (err.inv ? ' invalid' : '')}>
        <label>
          Covered invoice <span className='req'>*</span>
        </label>
        <SearchSelect
          value={invId}
          onChange={(v) => {
            setInvId(v)
            setErr((p) => ({...p, inv: false}))
          }}
          options={eligible.map((i) => ({
            value: i.id,
            label: i.id + ' · ' + i.partner + ' · net ' + inr(i.billedAmount - (i.cnAmount || 0)),
          }))}
          placeholder={
            eligible.length
              ? 'Select a reconciled invoice…'
              : 'No reconciled invoices awaiting payment'
          }
          invalid={!!err.inv}
        />
      </div>

      <div className={'field' + (err.txn ? ' invalid' : '')}>
        <label>
          Transaction ID (UTR / NEFT / RTGS) <span className='req'>*</span>
        </label>
        <input
          className={'input' + (err.txn ? ' err' : '')}
          placeholder='e.g. UTR4413009922'
          value={txn}
          onChange={(e) => {
            setTxn(e.target.value)
            setErr((p) => ({...p, txn: false}))
          }}
        />
      </div>

      <div className={'field' + (err.date ? ' invalid' : '')}>
        <label>
          Payment date <span className='req'>*</span>
        </label>
        <input
          className={'input' + (err.date ? ' err' : '')}
          type='date'
          value={date}
          onChange={(e) => {
            setDate(e.target.value)
            setErr((p) => ({...p, date: false}))
          }}
        />
      </div>

      <div className={'field' + (err.bank ? ' invalid' : '')} style={{marginBottom: 0}}>
        <label>
          Bank name <span className='req'>*</span>
        </label>
        <SearchSelect
          value={bank}
          onChange={(v) => {
            setBank(v)
            setErr((p) => ({...p, bank: false}))
          }}
          options={BANKS}
          placeholder='Select bank…'
          invalid={!!err.bank}
        />
      </div>
    </Modal>
  )
}

RecordPaymentModal.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
  onRecorded: PropTypes.func,
  toast: PropTypes.func,
}
